/**
 * A directed lane graph built from the road segments the world already
 * declares.
 *
 * Pure data and arithmetic - no three.js, no DOM - so the traffic logic that
 * sits on top of it can be unit-tested without a browser. The alternative was
 * hand-authoring lane data alongside `ROADS`, which would have been two
 * sources of truth for the same streets and would silently disagree the first
 * time a road moved.
 *
 * Every segment in `ROADS` is axis-aligned, and the construction here relies
 * on that. It is not a general road-graph builder and does not pretend to be:
 * curves or diagonals would need a different intersection test, and the check
 * at the bottom of `buildRoadNetwork` fails loudly rather than producing a
 * graph with holes in it.
 */
import { ROADS, type RoadSegment } from '../../sim/data/world.js';

/** How far a lane sits from the centre of its road, in metres. */
const LANE_OFFSET_FACTOR = 0.26;

export interface LaneNode {
  id: number;
  x: number;
  z: number;
  /** Lane ids that arrive here. */
  incoming: number[];
  /** Lane ids that leave here. */
  outgoing: number[];
  /** True where more than two roads meet, so give-way applies. */
  junction: boolean;
}

export interface Lane {
  id: number;
  fromNode: number;
  toNode: number;
  /** Start and end in world space, already offset to the correct side. */
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  length: number;
  /** Radians, in the game's convention: 0 is +Z, PI/2 is +X. */
  heading: number;
}

export interface RoadNetwork {
  nodes: LaneNode[];
  lanes: Lane[];
  /** Point along a lane, 0..1. */
  pointOn(lane: Lane, t: number): { x: number; z: number };
}

export function buildRoadNetwork(segments: readonly RoadSegment[] = ROADS): RoadNetwork {
  const horizontal = segments.filter((s) => Math.abs(s.z1 - s.z2) < 1e-6);
  const vertical = segments.filter((s) => Math.abs(s.x1 - s.x2) < 1e-6);
  if (horizontal.length + vertical.length !== segments.length) {
    throw new Error(
      'buildRoadNetwork only handles axis-aligned segments; ' +
      'a diagonal road needs a different intersection test',
    );
  }

  // --- split points -------------------------------------------------------
  // Every segment is cut wherever a perpendicular one crosses it. Those cuts,
  // plus the two endpoints, are the nodes of the graph.
  const cuts = new Map<RoadSegment, number[]>();
  for (const s of segments) cuts.set(s, [along(s, s.x1, s.z1), along(s, s.x2, s.z2)]);

  for (const h of horizontal) {
    for (const v of vertical) {
      const x = v.x1;
      const z = h.z1;
      if (!within(x, h.x1, h.x2) || !within(z, v.z1, v.z2)) continue;
      cuts.get(h)!.push(along(h, x, z));
      cuts.get(v)!.push(along(v, x, z));
    }
  }

  const nodes: LaneNode[] = [];
  const nodeByKey = new Map<string, LaneNode>();
  function nodeAt(x: number, z: number): LaneNode {
    // Quantised so two segments meeting at the same point share one node
    // rather than producing two that are a floating-point hair apart.
    const key = `${Math.round(x * 100)}:${Math.round(z * 100)}`;
    let node = nodeByKey.get(key);
    if (!node) {
      node = { id: nodes.length, x, z, incoming: [], outgoing: [], junction: false };
      nodes.push(node);
      nodeByKey.set(key, node);
    }
    return node;
  }

  const lanes: Lane[] = [];
  for (const s of segments) {
    const offset = s.width * LANE_OFFSET_FACTOR;
    const ordered = [...new Set(cuts.get(s)!)].sort((a, b) => a - b);

    for (let i = 0; i < ordered.length - 1; i++) {
      const a = pointAt(s, ordered[i]!);
      const b = pointAt(s, ordered[i + 1]!);
      // Both directions. Right-hand traffic: with forward crossed into up,
      // the right-hand side is +Z for a car heading +X and -X for one heading
      // +Z, and the lane is pushed that way so oncoming traffic passes on the
      // correct side rather than through itself.
      addLane(a, b, offset);
      addLane(b, a, offset);
    }
  }

  function addLane(
    a: { x: number; z: number },
    b: { x: number; z: number },
    offset: number,
  ): void {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const length = Math.hypot(dx, dz);
    if (length < 0.5) return;
    const heading = Math.atan2(dx, dz);
    // Right of the direction of travel, which is forward x up. With forward =
    // (sin h, 0, cos h) and up = +Y that works out to (-cos h, 0, sin h).
    // Getting this backwards is not a crash - it produces a perfectly
    // consistent network where everyone drives on the left, against the road
    // markings and the parked cars.
    const rx = -Math.cos(heading);
    const rz = Math.sin(heading);
    const from = nodeAt(a.x + rx * offset, a.z + rz * offset);
    const to = nodeAt(b.x + rx * offset, b.z + rz * offset);
    const lane: Lane = {
      id: lanes.length,
      fromNode: from.id,
      toNode: to.id,
      x1: from.x, z1: from.z, x2: to.x, z2: to.z,
      length: Math.hypot(to.x - from.x, to.z - from.z),
      heading,
    };
    lanes.push(lane);
    from.outgoing.push(lane.id);
    to.incoming.push(lane.id);
  }

  // --- connect across junctions -------------------------------------------
  // Lanes on different roads end at different offset points, so nothing joins
  // them yet. Any two lane endpoints within a junction's reach are treated as
  // the same place to turn through.
  const JOIN_RADIUS = 7.5;
  for (const node of nodes) {
    for (const other of nodes) {
      if (other.id === node.id) continue;
      if (Math.hypot(node.x - other.x, node.z - other.z) > JOIN_RADIUS) continue;
      for (const laneId of other.outgoing) {
        if (!node.outgoing.includes(laneId)) node.outgoing.push(laneId);
      }
    }
    // A node a car can leave by more than one lane is a place where paths
    // cross, which is where give-way has to apply.
    node.junction = node.outgoing.length > 1;
  }

  if (lanes.length === 0) {
    throw new Error('buildRoadNetwork produced no lanes; the road data is unusable');
  }

  return {
    nodes,
    lanes,
    pointOn(lane, t) {
      return {
        x: lane.x1 + (lane.x2 - lane.x1) * t,
        z: lane.z1 + (lane.z2 - lane.z1) * t,
      };
    },
  };
}

/** Distance of a point along a segment, as a 0..1 fraction. */
function along(s: RoadSegment, x: number, z: number): number {
  const dx = s.x2 - s.x1;
  const dz = s.z2 - s.z1;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) return 0;
  return ((x - s.x1) * dx + (z - s.z1) * dz) / len2;
}

function pointAt(s: RoadSegment, t: number): { x: number; z: number } {
  return { x: s.x1 + (s.x2 - s.x1) * t, z: s.z1 + (s.z2 - s.z1) * t };
}

function within(v: number, a: number, b: number): boolean {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  // Strictly inside, with a small margin: a crossing exactly at an endpoint is
  // already a node and does not need cutting again.
  return v > lo + 0.01 && v < hi - 0.01;
}
